import ArticleHero from '@components/marketing-website/ArticleHero'
import ArticleSidebar from '@components/marketing-website/ArticleSidebar'
import Footer from '@components/marketing-website/Footer'
import NavBar from '@components/marketing-website/NavBar'
import StickyMobileButton from '@components/marketing-website/StickyMobileButton'
import MenuLogo from '@icons/MenuLogo'
import MainLayout from '@layouts/MainLayout'
import { WebsiteLayoutProps } from '@layouts/WebsiteLayout/props'
import { routes } from '@routes/index'
import { isUserLoggedIn } from '@utils/isUserLoggedIn'
import { graphql, useStaticQuery } from 'gatsby'
import { GatsbyImage } from 'gatsby-plugin-image'
import React, { FC } from 'react'
import { Main, PageWrapper, SectionWrapper } from './styles'

const WebsiteLayout: FC<WebsiteLayoutProps> = ({ articleHero, sidebar, staticArticleHero, children }) => {
  const { strapiSetting } = useStaticQuery(graphql`
    fragment MenuItem on StrapiComponentMenuMenuItem {
      id
      label
      type
      url
      page {
        slug
      }
    }

    fragment StrapiMenu on StrapiMenu {
      menu {
        ...MenuItem
        ... on StrapiComponentMenuSubmenu {
          menu_item {
            ...MenuItem
          }
          items {
            ...MenuItem
          }
        }
        ... on StrapiComponentMenuSubsubmenu {
          menu_item {
            ...MenuItem
          }
          items {
            menu_item {
              ...MenuItem
            }
            items {
              ...MenuItem
            }
          }
        }
      }
    }

    query {
      strapiSetting {
        main_menu {
          desktop_menu {
            ...StrapiMenu
          }
          mobile_menu {
            ...StrapiMenu
          }
        }
        footer {
          menu {
            menu {
              ... on StrapiComponentMenuSubmenu {
                id
                items {
                  page {
                    slug
                  }
                  label
                  url
                }
              }
            }
          }
        }
      }
    }
  `)

  const footerData = strapiSetting?.footer?.menu?.menu ?? null

  const getUrlForMenuItem = (item: any) =>
    item?.type === 'page' && item?.page?.slug
      ? `${item?.page?.parent?.slug ? `/${item?.page?.parent?.slug}` : ''}/${item.page.slug}`
      : item?.url

  const formatDesktopMenuItems = (menuItem: any) => {
    const item = menuItem?.menu_item || menuItem
    const subMenus = menuItem?.items?.map((submenu: any) => ({
      label: submenu?.menu_item?.label,
      links: submenu?.items?.map((item: any) => ({
        label: item?.label,
        url: getUrlForMenuItem(item),
      })),
    }))

    return {
      label: item?.label,
      url: getUrlForMenuItem(item),
      subMenus: subMenus,
    }
  }

  const formatMobileMenuItems = (menuItem: any) => {
    const item = menuItem?.menu_item || menuItem
    const links = menuItem?.items?.map((item: any) => ({
      label: item?.label,
      url: getUrlForMenuItem(item),
    }))

    return {
      label: item?.label,
      links: links,
    }
  }

  const isLoggedIn = isUserLoggedIn()

  return (
    <MainLayout>
      <NavBar
        logo={<MenuLogo />}
        desktopMenu={strapiSetting?.main_menu?.desktop_menu?.menu?.map(formatDesktopMenuItems)}
        mobileMenu={strapiSetting?.main_menu?.mobile_menu?.menu?.map(formatMobileMenuItems)}
        buttons={[
          {
            variant: 'roundedPrimary',
            label: 'Doe de gratis WOZ Check',
            link: '/woz-check',
            linkType: 'wozCheckCta',
          },
          {
            variant: 'roundedSecondary',
            label: isLoggedIn ? 'Mijn Eerlijke WOZ' : 'Login',
            link: isLoggedIn ? routes.mewoz.home : routes.signIn,
          },
        ]}
      />
      <Main>
        {articleHero && (
          <ArticleHero
            image={
              !articleHero.image ? null : articleHero?.image?.type === 'img' ? (
                articleHero?.image
              ) : (
                <GatsbyImage
                  image={articleHero?.image?.file?.childImageSharp?.gatsbyImageData!}
                  alt={articleHero?.image?.alternativeText || ''}
                />
              )
            }
            title={articleHero?.title!}
          />
        )}
        {staticArticleHero}
        {sidebar ? (
          <PageWrapper>
            <ArticleSidebar links={sidebar.items} />
            <SectionWrapper hasSidebar={!!sidebar}>{children}</SectionWrapper>
          </PageWrapper>
        ) : articleHero || staticArticleHero ? (
          <SectionWrapper hasSidebar={!!sidebar}>{children}</SectionWrapper>
        ) : (
          <>{children}</>
        )}
        {footerData && (
          <Footer
            hasArticleHero={!!articleHero || !!staticArticleHero}
            navigationLinks={footerData[0].items}
            wozLinks={footerData[1].items}
            generalLinks={footerData[2].items}
          />
        )}
      </Main>
      <StickyMobileButton
        button={{
          variant: 'primary',
          linkType: 'internal',
          link: '#',
          label: 'Doe de gratis WOZ Check',
        }}
      />
    </MainLayout>
  )
}

export default WebsiteLayout
